<script lang="ts">
  // BJL-CHECK: Badge (used, per feed and for the collapsed alarm count), Collapsible, Button
  // — both considered for the collapse control. This panel floats over the map with
  // `pointer-events: none` so a pan started on top still reaches MapLibre, and only the
  // control opts back in; a Collapsible would make the whole expanded body interactive and
  // swallow pans in the top-left corner. Button brings its own sizing and would lose the
  // glass surface the floating controls share. See artifacts/deviations/FeedHealthPanel.md.
  //
  // Upstream feed freshness, shown over the map when the "Feed health" layer is on.
  //
  // The point is to distinguish two things the map can't otherwise tell apart: empty track
  // because nothing is running, and empty track because the feed carrying it stopped
  // answering. Without this, a dead ACE feed looks exactly like a quiet night on the A.
  //
  // Ages advance on a local 1 Hz tick, not only on each SYSTEM_STATUS push, so a backend
  // that stops publishing shows its feeds aging out rather than frozen at "12s".
  import { Badge } from '@byjackli-labs/ui/Badge'
  import { Colors } from '../../ui/colors'
  import { GLASS_BG, panelFrame, Type } from '../../ui/theme'
  import { safeTop } from '../../ui/safeArea'
  import { useSystemStore, isFeedStale, liveAgeSeconds } from '../../store/system'
  import { useLayersStore } from '../../store/layers'
  import { storeSelect } from '../../lib/store.svelte'

  function ageText(seconds: number | null): string {
    if (seconds === null) return 'never'
    const s = Math.floor(seconds)
    if (s < 60) return `${s}s`
    const mins = Math.floor(s / 60)
    if (mins < 60) return `${mins}m`
    return `${Math.floor(mins / 60)}h`
  }

  // Local, not a layer-store flag: a view preference for this one readout. The component
  // outlives the layer toggle, so it survives an off-and-on without anything persisting it.
  //
  // Starts collapsed to a 40px puck; the rider opens it only to learn WHICH feed is behind
  // an empty stretch. The alarm still reaches them collapsed (see `unhealthy`), so the list
  // is detail-on-demand rather than something to dismiss.
  let collapsed = $state(true)

  const enabled = storeSelect(useLayersStore, (s) => s.feedHealth)
  const status = storeSelect(useSystemStore, (s) => s.status)
  const receivedAtMs = storeSelect(useSystemStore, (s) => s.receivedAtMs)

  // `liveAgeSeconds` reads performance.now(), which nothing can subscribe to, so this 1 Hz
  // tick is what recomputes the rows. Cheap — a handful of rows, outside the map's animation
  // loop — and it runs while collapsed too, or the alarm count would never light up.
  let tick = $state(0)
  $effect(() => {
    if (!enabled.value) return
    const timer = setInterval(() => { tick += 1 }, 1_000)
    return () => clearInterval(timer)
  })

  const rows = $derived.by(() => {
    tick
    const at = receivedAtMs.value
    return (status.value?.feeds ?? []).map((feed) => {
      const stale = isFeedStale(feed, at)
      const color = !feed.ok ? Colors.danger : stale ? Colors.warning : Colors.success
      return {
        key: `${feed.kind}:${feed.id}`,
        id: feed.id,
        error: feed.error ?? '',
        color,
        valueColor: !feed.ok || stale ? color : Colors.textMuted,
        text: feed.ok ? ageText(liveAgeSeconds(feed, at)) : 'DOWN',
      }
    })
  })

  // Collapsing must not hide what the panel exists to report, so a down or stale feed shows
  // as a count on the puck. Green stays silent — "all fine" is inferable from the map.
  const unhealthy = $derived(
    rows.filter((r) => r.color !== Colors.success).length,
  )

  // Down outranks stale: red plus yellow is a red puck, because the rider's next question
  // ("is this map lying to me?") has the same answer either way.
  const alarmColor = $derived(
    rows.some((r) => r.color === Colors.danger) ? Colors.danger : Colors.warning,
  )
</script>

<!-- The dial that stands in for the word "Feeds": a transmitter dot throwing two pairs of
     waves. It inherits `currentColor`, so the button tints it — muted at rest, the alarm
     color when a feed is down or stale — and it scales from one viewBox at both the puck's
     20px and the expanded header's 16px. -->
{#snippet feedIcon(size: number)}
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="1.9"
    stroke-linecap="round"
    aria-hidden="true"
    style="display: block;"
  >
    <circle cx="12" cy="12" r="2.1" fill="currentColor" stroke="none" />
    <path d="M8.2 8.2a5.4 5.4 0 0 0 0 7.6" />
    <path d="M15.8 15.8a5.4 5.4 0 0 0 0-7.6" />
    <path d="M5.2 5.2a9.6 9.6 0 0 0 0 13.6" />
    <path d="M18.8 18.8a9.6 9.6 0 0 0 0-13.6" />
  </svg>
{/snippet}

{#if enabled.value}
  <!-- Top-left, clear of the centered filter pills and the top-right NavigationControl. The
       two states are laid out from the same corner, so the puck grows into the panel from
       where it stood rather than jumping. -->
  {#if collapsed}
    <!-- Collapsed: a 40px puck, matching the floating map buttons on the other edge. Unlike
         the expanded panel there is nothing here but the control, so the button itself takes
         the pointer events and no transparent wrapper is needed. It is small enough that
         swallowing a pan that starts exactly on it is not a real cost. -->
    <button
      type="button"
      onclick={() => { collapsed = false }}
      aria-expanded="false"
      aria-label={unhealthy > 0 ? `Feed health — ${unhealthy} feed(s) down or stale` : 'Feed health'}
      style="
        position: absolute; top: {safeTop(16)}; left: 12px;
        z-index: 50;
        box-sizing: border-box; width: 40px; height: 40px;
        display: flex; align-items: center; justify-content: center;
        background-color: {GLASS_BG};
        border-radius: 50%;
        border: 1px solid {unhealthy > 0 ? alarmColor : Colors.borderStrong};
        color: {unhealthy > 0 ? alarmColor : Colors.textSecondary};
        backdrop-filter: blur(8px);
        padding: 0; cursor: pointer; line-height: 1;
      "
    >
      {@render feedIcon(20)}
      <!-- The alarm survives the collapse, now as a corner badge instead of a number beside a
           word — the puck has no room to spell out what it is counting, so the count sits
           where the layers button puts its own (MapPage), and the icon tint carries the tone. -->
      {#if unhealthy > 0}
        <span style="position: absolute; top: -5px; right: -5px;">
          <Badge size="xs" variant="solid" tone={alarmColor} shape="pill" register="mono">
            {unhealthy}
          </Badge>
        </span>
      {/if}
    </button>
  {:else}
    <!-- Expanded: the full readout, unchanged. pointer-events: none so it never intercepts a
         map pan; only the header opts back in. -->
    <div
      style="
        position: absolute; top: {safeTop(16)}; left: 12px;
        z-index: 50;
        background-color: {GLASS_BG};
        {panelFrame()}
        padding: 8px 12px 10px;
        backdrop-filter: blur(8px);
        display: flex; flex-direction: column;
        pointer-events: none;
      "
    >
      <!-- The header is the collapse control, so it — and only it — takes pointer events back
           from the container. It carries the same icon the puck does, so the thing tapped to
           open the panel is still visible in it, and the ▴ says which way it closes. The rows
           below hold the width; the header just spans it. -->
      <button
        type="button"
        onclick={() => { collapsed = true }}
        aria-expanded="true"
        aria-label="Feed health"
        style="
          pointer-events: auto;
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          background: none; border: none; padding: 0; margin: 0;
          color: {unhealthy > 0 ? alarmColor : Colors.textSecondary};
          cursor: pointer; white-space: nowrap;
        "
      >
        {@render feedIcon(16)}
        <span style="color: {Colors.textSecondary}; font-size: 12px; line-height: 1;">▴</span>
      </button>
      <div style="display: flex; flex-direction: column; gap: 6px; padding-top: 6px;">
        {#if rows.length === 0}
          <span style="color: {Colors.textMuted}; {Type.caption};">Waiting for status…</span>
        {:else}
          {#each rows as row (row.key)}
            <!-- One bjl Badge per feed: the status dot is the component's own, tinted by the
                 feed's health, and `register="mono"` keeps the ages from jittering as they tick. -->
            <div
              style="display: flex; align-items: center; gap: 6px; white-space: nowrap;"
              title={row.error}
            >
              <Badge size="xs" variant="soft" dot={row.color} tone={row.color} register="mono">
                {row.id}
              </Badge>
              <span style="color: {row.valueColor}; font-size: var(--bjl-font-size-xs); font-variant-numeric: tabular-nums;">
                {row.text}
              </span>
            </div>
          {/each}
        {/if}
      </div>
    </div>
  {/if}
{/if}
